import classNames from 'classnames';
import ProjectSelect from 'components/ProjectSelect';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { setHourDraftForecastId, setHourDraftProject } from 'redux/modules/hours.actions';
import { fetchPinnedProjects } from 'redux/modules/pins.actions';
import {
  createProjectDraft,
  fetchAllWorkspaceProjects,
  searchProjects,
} from 'redux/modules/projects.actions';
import { saveUserProperty } from 'redux/modules/user.actions';
import { getAllProjectsLoggingEnabled } from 'redux/selectors/accounts';
import { getHourEditorProjectSectionProps } from 'redux/selectors/hours.draft.project';
import type { ActAsUserIdOwnProps } from 'redux/selectors/projects';
import type { IState } from 'redux/state';
import type { HourEditorProjectSectionProps } from 'types/hour';
import type { ProjectBase } from 'types/project';
import type { MappedActions } from 'types/redux';
import { ForecastSuggestions } from './ForecastSuggestions';
import styles from './ProjectSection.module.scss';
import { ProjectSuggestions } from './ProjectSuggestions';
import { Section } from './Section';

interface OwnProps extends ActAsUserIdOwnProps {
  disabled: boolean;
  hasSelectedProject: boolean;
  noBorder?: boolean;
  standalone?: boolean;
  maxHeight?: number;
  forecastId?: number | null;
}

type StateProps = HourEditorProjectSectionProps & {
  allProjectsLoggingEnabled: boolean;
};
const mapStateToProps = (state: IState): StateProps => ({
  ...getHourEditorProjectSectionProps(state),
  allProjectsLoggingEnabled: getAllProjectsLoggingEnabled(state),
});

const mapActionsToProps = {
  saveUserProperty,
  setHourDraftProject,
  setHourDraftForecastId,
  createProjectDraft,
  searchProjects,
  fetchPinnedProjects,
  fetchAllWorkspaceProjects,
};

type ActionProps = MappedActions<typeof mapActionsToProps>;

type Props = OwnProps & StateProps & ActionProps;

class ProjectSection extends PureComponent<Props> {
  static defaultProps = {
    disabled: false,
  };

  hiddenInputRef = React.createRef<HTMLInputElement>();

  componentDidMount() {
    this.props.fetchPinnedProjects();
    // The action itself is idempotent within a session; safe to dispatch
    // on every hour-editor open.
    if (this.props.allProjectsLoggingEnabled) {
      this.props.fetchAllWorkspaceProjects();
    }
  }

  handleProjectChange = (projectId: number, forecastId?: number) => {
    // Re-introduce this on save, if at all (we don't currently care about this property)
    // if (this.props.ui.selectedProjectId) {
    //   this.props.saveUserProperty(DEFAULT_PROJECT_ID, this.props.ui.selectedProjectId);
    // }
    this.props.setHourDraftProject(projectId);
    this.props.setHourDraftForecastId(forecastId);
    this.hiddenInputRef.current?.focus();
  };

  handleSelectSuggestedProject = (projectId: number) => {
    this.handleProjectChange(projectId);
    this.hiddenInputRef.current?.focus();
  };

  handleProjectSelect = (project: ProjectBase, forecastId?: number) => {
    this.handleProjectChange(project.id, forecastId);
  };

  handleCreateNewProject = (payload: { name: string }) => {
    this.props.createProjectDraft(payload);
  };

  handleSearch = (filterString: string) => {
    return this.props.searchProjects(
      filterString,
      this.props.userId,
      'active',
      this.props.allProjectsLoggingEnabled ? 'global' : undefined
    );
  };

  render() {
    const {
      ui,
      recentProjects,
      pinnedProjects,
      projects,
      forecasts,
      suggestions,
      standalone,
      clients,
      currency,
    } = this.props;

    return (
      <Section
        style={{ position: 'relative', zIndex: 10 }}
        noBorder={!this.props.hasSelectedProject || this.props.noBorder}
        noBottom={!this.props.hasSelectedProject || this.props.noBorder}
        standalone={standalone}
      >
        <ProjectSelect
          usePortal
          disabled={this.props.disabled}
          placeholder={ui.placeholder}
          isLoading={ui.isLoading}
          currencySymbol={currency.symbol}
          clients={clients}
          projects={projects}
          recentProjects={recentProjects}
          forecasts={forecasts}
          pinnedProjects={pinnedProjects}
          onChange={this.handleProjectSelect}
          allowCreate={ui.allowCreateProject}
          onCreateNew={this.handleCreateNewProject}
          value={ui.selectedProjectId}
          forecastId={this.props.forecastId}
          onSearch={this.handleSearch}
          inputClassName={classNames({ [styles.projectInput]: !ui.selectedProjectId })}
          overlayClassName={classNames({ [styles.overlay]: !ui.selectedProjectId })}
          hideColorBox={!ui.selectedProjectId}
          placeholderColor="#8e9cad"
          popoverFixed
          allProjectsLoaded={ui.allProjectsLoaded}
          maxHeight={this.props.maxHeight}
        />
        {ui.showForecastSuggestions && (
          <ForecastSuggestions
            showPlaceholders={ui.isLoading}
            size="large"
            onChange={this.handleProjectChange}
            suggestions={forecasts}
            count={ui.numForecastSuggestions}
          />
        )}
        {ui.showProjectSuggestions && (
          <ProjectSuggestions
            showPlaceholders={ui.isLoading}
            size="large"
            onChange={this.handleSelectSuggestedProject}
            suggestions={suggestions}
            count={ui.numProjectSuggestions}
          />
        )}
        <input
          // This input is used to take focus after a suggestion is selected.
          // If we don't do this, the activeElement is unmounted and <body> is the new activeElement
          // Without this, the user can't press enter after selecting a suggested prroject
          type="text"
          tabIndex={-1}
          style={{ opacity: '0', position: 'absolute', left: '-100%' }}
          ref={this.hiddenInputRef}
          autoComplete="off"
        />
      </Section>
    );
  }
}

export default connect(mapStateToProps, mapActionsToProps)(ProjectSection);
